import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  GenerateMenuPdfResultSchema,
  MenuListResponseSchema,
  MenuRowSchema,
  type AddMenuRowInput,
  type DeleteMenuRowInput,
  type GenerateMenuPdfResult,
  type MenuRow,
  type PatchMenuRowInput,
} from "@/api/types/menu";

/** Query-key factory — mirrors the events hook convention. */
export const eventMenuKeys = {
  all: ["event-menu"] as const,
  list: (eventId: string | undefined) => [...eventMenuKeys.all, eventId] as const,
};

function invalidateMenu(queryClient: QueryClient, eventId: string) {
  void queryClient.invalidateQueries({ queryKey: eventMenuKeys.list(eventId) });
}

/** Flat list — the contract has no cursor field on GET /v2/events/:id/menu. */
export function useEventMenu(eventId: string | undefined) {
  return useQuery<MenuRow[]>({
    queryKey: eventMenuKeys.list(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}/menu`);
      return validateResponse(
        MenuListResponseSchema,
        data,
        "GET /v2/events/:id/menu",
      ).items;
    },
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}

export function useAddMenuRow() {
  const queryClient = useQueryClient();
  return useMutation<MenuRow, unknown, AddMenuRowInput>({
    mutationFn: async ({ eventId, ...body }) => {
      const { data } = await apiClient.post(`/v2/events/${eventId}/menu`, body);
      return validateResponse(MenuRowSchema, data, "POST /v2/events/:id/menu");
    },
    onSuccess: (_row, variables) => {
      invalidateMenu(queryClient, variables.eventId);
    },
  });
}

export function usePatchMenuRow() {
  const queryClient = useQueryClient();
  return useMutation<MenuRow, unknown, PatchMenuRowInput>({
    mutationFn: async ({ eventId, rowId, ...patch }) => {
      const { data } = await apiClient.patch(
        `/v2/events/${eventId}/menu/${rowId}`,
        patch,
      );
      return validateResponse(MenuRowSchema, data, "PATCH /v2/events/:id/menu/:row_id");
    },
    onSuccess: (_row, variables) => {
      invalidateMenu(queryClient, variables.eventId);
    },
  });
}

export function useDeleteMenuRow() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, DeleteMenuRowInput>({
    mutationFn: async ({ eventId, rowId }) => {
      await apiClient.delete(`/v2/events/${eventId}/menu/${rowId}`);
    },
    onSuccess: (_void, variables) => {
      invalidateMenu(queryClient, variables.eventId);
    },
  });
}

/**
 * Generates the menu / story-cards PDF. Contract: "return {pdf_key} or a
 * URL" — we validate loosely (both optional) and let the caller prefer
 * `url` when present, falling back to a GET on the same collection path
 * (mirroring the quotes feature's `/pdf` GET-after-generate convention)
 * when only a bare `pdf_key` comes back. See MenuShoppingListCard.
 */
export function useGenerateMenuPdf() {
  return useMutation<GenerateMenuPdfResult, unknown, string>({
    mutationFn: async (eventId) => {
      const { data } = await apiClient.post(`/v2/events/${eventId}/menu/pdf`, {});
      return validateResponse(
        GenerateMenuPdfResultSchema,
        data,
        "POST /v2/events/:id/menu/pdf",
      );
    },
  });
}
